import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart, hydrateCart, removeBundle } from "@/lib/cart";
import { ShoppingBag, X, ArrowRight, ArrowLeft, Plus, Minus, ArrowsClockwise } from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Cart() {
  const cart = useCart();
  const empty = cart.items.length === 0;

  // Heal legacy cart items missing price/image (revenue-blocking $0 subtotal bug).
  useEffect(() => {
    hydrateCart(BACKEND).catch((err) => console.warn("Cart: hydration failed:", err));
  }, []);

  return (
    <main data-testid="cart-page" className="min-h-screen bg-[#0A0A0A] text-white font-body pt-16">
      <Nav />
      <div className="border-b border-white/10 px-6 md:px-12 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/shop" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
            <ArrowLeft size={12} weight="bold" /> Keep shopping
          </Link>
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40">/ Your cart</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <p className="text-[#B32027] uppercase tracking-[0.4em] text-[10px] font-bold mb-3">/ Cart</p>
        <h1 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] mb-8">
          The bag<br /><span className="text-[#B32027]">carries names.</span>
        </h1>

        {empty ? (
          <div data-testid="cart-empty" className="border-2 border-white/15 bg-[#0F0F0F] p-10 md:p-16 text-center space-y-6">
            <ShoppingBag size={48} weight="thin" className="text-white/30 mx-auto" />
            <p className="text-white/70 text-lg">Your cart is empty.</p>
            <Link to="/shop" data-testid="cart-shop-cta" className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.3em]">
              Browse the shop <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_360px] gap-10">
            <div className="space-y-4">
              {/* Group bundle lines under a bundle header; non-bundle lines render as-is */}
              {(() => {
                const seen = new Set();
                const rows = [];
                for (const it of cart.items) {
                  if (it.bundle_slug) {
                    if (seen.has(it.bundle_slug)) continue;
                    seen.add(it.bundle_slug);
                    const lines = cart.items.filter((x) => x.bundle_slug === it.bundle_slug);
                    rows.push(<BundleGroup key={`bg-${it.bundle_slug}`} lines={lines} cart={cart} />);
                  } else {
                    rows.push(<CartLine key={`${it.sync_variant_id}-nb`} item={it} cart={cart} />);
                  }
                }
                return rows;
              })()}
            </div>

            <aside className="md:sticky md:top-24 self-start border-2 border-[#B32027] bg-[#0F0F0F] p-5 space-y-4 h-fit">
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027]">/ Summary</p>
              <div className="text-sm space-y-2">
                <div className="flex justify-between text-white/70">
                  <span>Items ({cart.count})</span>
                  <span className="text-white font-semibold" data-testid="cart-subtotal">${cart.subtotal.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-white/40 uppercase tracking-[0.2em]">Shipping calculated at checkout</p>
              </div>
              <Link
                to="/merch/checkout?cart=1"
                data-testid="cart-checkout"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em]"
              >
                Checkout · ${cart.subtotal.toFixed(2)} <ArrowRight size={12} weight="bold" />
              </Link>
              <button onClick={cart.clear} data-testid="cart-clear" className="w-full text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-red-400 py-1">
                Clear cart
              </button>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}

function CartLine({ item, cart }) {
  const [variantsOpen, setVariantsOpen] = useState(false);
  const [variants, setVariants] = useState(null);
  const [loading, setLoading] = useState(false);

  const openVariants = async () => {
    if (variantsOpen) { setVariantsOpen(false); return; }
    setVariantsOpen(true);
    if (variants) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/shop/products/${item.product_id}`);
      const d = await r.json();
      if (r.ok && d.variants) setVariants(d.variants);
    } catch (e) {
      console.error("variant fetch failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const pick = (v) => {
    cart.swap(item.sync_variant_id, {
      sync_variant_id: v.id,
      variant_id: v.variant_id,
      item_retail_price: Number(v.retail_price || item.item_retail_price),
      product_name: item.product_name,
      variant_name: v.name || "",
      image: v.preview_url || item.image || "",
    });
    setVariantsOpen(false);
  };

  const isBundleItem = !!item.bundle_slot_key || !!item.bundle_slug;

  return (
    <article data-testid={`cart-item-${item.sync_variant_id}${item.bundle_slot_key ? `-${item.bundle_slot_key}` : ""}`} className="border border-white/15 bg-[#0F0F0F] p-4">
      <div className="flex gap-4">
        <div className="w-24 h-24 bg-white flex-shrink-0 overflow-hidden">
          {item.image ? <img src={item.image} alt="" className="w-full h-full object-contain" /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm leading-snug">
            {item.product_name}
            {isBundleItem && (
              <span className="ml-2 text-[9px] uppercase tracking-[0.25em] font-bold bg-[#B32027] text-white px-1.5 py-0.5">Bundle</span>
            )}
          </p>
          {item.variant_name && !isBundleItem && (
            <button
              type="button"
              onClick={openVariants}
              data-testid={`cart-swap-${item.sync_variant_id}`}
              className="mt-1 inline-flex items-center gap-1.5 text-white/55 hover:text-[#B32027] text-xs group"
            >
              <span>{item.variant_name}</span>
              <ArrowsClockwise size={11} weight="bold" className="opacity-60 group-hover:opacity-100" />
              <span className="text-[9px] uppercase tracking-[0.2em] font-bold">Change</span>
            </button>
          )}
          {item.variant_name && isBundleItem && (
            <p className="mt-1 text-white/55 text-xs">{item.variant_name}</p>
          )}
          <p className="text-white/70 text-sm mt-2">
            {item.is_free_gift ? "FREE" : `$${Number(item.item_retail_price).toFixed(2)}`}
          </p>
          <div className="mt-3 flex items-center gap-3">
            {isBundleItem ? (
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/45">Qty 1 · part of bundle</span>
            ) : (
              <>
                <button onClick={() => cart.update(item.sync_variant_id, item.quantity - 1)} data-testid={`cart-dec-${item.sync_variant_id}`} className="w-8 h-8 border border-white/20 hover:border-[#B32027] flex items-center justify-center">
                  <Minus size={12} weight="bold" />
                </button>
                <span className="w-8 text-center font-bold" data-testid={`cart-qty-${item.sync_variant_id}`}>{item.quantity}</span>
                <button onClick={() => cart.update(item.sync_variant_id, item.quantity + 1)} data-testid={`cart-inc-${item.sync_variant_id}`} className="w-8 h-8 border border-white/20 hover:border-[#B32027] flex items-center justify-center">
                  <Plus size={12} weight="bold" />
                </button>
              </>
            )}
            <button
              onClick={() => {
                if (isBundleItem && item.bundle_slug) {
                  // Removing any line of a bundle removes the whole bundle
                  removeBundle(item.bundle_slug);
                } else {
                  cart.remove(item.sync_variant_id, item.bundle_slot_key || null);
                }
              }}
              data-testid={`cart-remove-${item.sync_variant_id}${item.bundle_slot_key ? `-${item.bundle_slot_key}` : ""}`}
              className="ml-auto text-white/40 hover:text-red-400 text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1"
            >
              <X size={12} weight="bold" /> {isBundleItem ? "Remove bundle" : "Remove"}
            </button>
          </div>
        </div>
        <p className="text-white font-bold whitespace-nowrap">
          {item.is_free_gift ? "FREE" : `$${(Number(item.item_retail_price) * Number(item.quantity)).toFixed(2)}`}
        </p>
      </div>

      {variantsOpen && (
        <div className="mt-4 border-t border-white/10 pt-4" data-testid={`cart-variant-picker-${item.sync_variant_id}`}>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold mb-2">Pick a different size / color</p>
          {loading && <p className="text-white/45 text-xs">Loading options…</p>}
          {variants && (
            <div className="flex flex-wrap gap-1.5">
              {variants.map((v) => {
                const isCurrent = Number(v.id) === Number(item.sync_variant_id);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => !isCurrent && pick(v)}
                    disabled={isCurrent}
                    data-testid={`cart-variant-option-${v.id}`}
                    className={`text-[11px] px-2.5 py-1.5 border-2 transition-colors ${
                      isCurrent
                        ? "border-[#B32027] bg-[#B32027]/15 text-white cursor-default"
                        : "border-white/15 text-white/75 hover:border-[#B32027] hover:text-white"
                    }`}
                  >
                    {v.name || `${v.color || ""} ${v.size || ""}`.trim() || `#${v.id}`}
                    {isCurrent && <span className="ml-1 text-[9px] uppercase tracking-[0.2em]">· picked</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </article>
  );
}



function BundleGroup({ lines }) {
  const first = lines[0] || {};
  const bundleName = first.bundle_name || "Legacy Bundle";
  const couponCode = first.bundle_coupon_code || "";
  // Retail total = sum of prices reported by the backend for the ORIGINAL retail
  // (before pro-rating). We stored the whole bundle original subtotal on each
  // line so we don't have to re-fetch. Fall back to summing displayed prices.
  const originalSubtotal = Number(first.bundle_original_subtotal || 0) || lines.reduce((s, l) => s + Number(l.item_retail_price || 0) * Number(l.quantity || 1), 0);
  const bundlePrice = Number(first.bundle_price || 0) || lines.reduce((s, l) => s + Number(l.item_retail_price || 0) * Number(l.quantity || 1), 0);
  const savings = Math.max(0, Number(first.bundle_savings || (originalSubtotal - bundlePrice)));

  return (
    <section
      data-testid={`cart-bundle-group-${first.bundle_slug}`}
      className="border-2 border-[#B32027] bg-[#0F0F0F]"
    >
      <header className="flex items-center gap-3 border-b border-[#B32027]/60 bg-[#B32027]/10 p-4">
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027]">/ Bundle</span>
        <p className="flex-1 font-display font-black uppercase tracking-tight text-sm md:text-base">{bundleName}</p>
        {couponCode && (
          <span data-testid={`cart-bundle-coupon-${first.bundle_slug}`} className="font-mono text-[10px] bg-[#B32027] text-white px-2 py-1 uppercase tracking-[0.2em] font-bold">
            {couponCode} · applied
          </span>
        )}
        <button
          type="button"
          data-testid={`cart-bundle-remove-${first.bundle_slug}`}
          onClick={() => { removeBundle(first.bundle_slug); }}
          className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60 hover:text-red-400"
          title="Remove entire bundle"
        >
          Remove bundle
        </button>
      </header>
      <div className="p-4 space-y-2">
        {lines.map((li) => (
          <div key={`bl-${li.sync_variant_id}-${li.bundle_slot_key}`} data-testid={`cart-bundle-line-${li.sync_variant_id}-${li.bundle_slot_key}`} className="flex items-center gap-3 text-sm">
            <div className="w-12 h-12 bg-white flex-shrink-0 overflow-hidden">
              {li.image ? <img src={li.image} alt="" className="w-full h-full object-contain" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-[13px] leading-tight truncate">{li.product_name}</p>
              {li.variant_name && <p className="text-white/50 text-[11px] truncate">{li.variant_name}</p>}
            </div>
            <p className={`text-[11px] font-bold uppercase tracking-[0.15em] whitespace-nowrap ${li.is_free_gift || li.is_bundle_free_item ? "text-[#B32027]" : "text-white/50"}`}>
              {li.is_free_gift || li.is_bundle_free_item ? "FREE" : ""}
            </p>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10 p-4 space-y-1 text-sm">
        <div className="flex justify-between text-white/60">
          <span>Regular price</span>
          <span className="line-through" data-testid={`cart-bundle-retail-${first.bundle_slug}`}>${originalSubtotal.toFixed(2)}</span>
        </div>
        {savings >= 10 && (
          <div className="flex justify-between text-[#B32027] font-bold">
            <span>Bundle discount{couponCode ? ` · ${couponCode}` : ""}</span>
            <span data-testid={`cart-bundle-discount-${first.bundle_slug}`}>-${savings.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-white font-bold text-base pt-1 border-t border-white/10">
          <span>Bundle price</span>
          <span data-testid={`cart-bundle-price-${first.bundle_slug}`}>${bundlePrice.toFixed(2)}</span>
        </div>
        {savings >= 10 && (
          <p data-testid={`cart-bundle-savings-${first.bundle_slug}`} className="text-[#B32027] text-xs font-bold uppercase tracking-[0.2em] pt-1">You save ${savings.toFixed(2)}</p>
        )}
        <p className="text-white/40 text-[10px] pt-2">Bundle products are made on demand and may arrive in separate packages.</p>
      </div>
    </section>
  );
}
